// MARK://dhk8/pos/pages/dev/ComponentsGallery
// Galería viva de la librería de componentes NintaiGO — gemelo POS del catálogo
// de admin-shell (DS-3). Ruta dev /dev/components, NO producción. Mismo
// mini-framework: registro de secciones + sidebar por categorías + render
// perezoso de la sección activa. Cubre los 12 componentes de
// apps/pos/src/components + una sección "Por revisar" (DS-4).
import { useState } from 'react'

import { CatalogShell, CatalogBlock, CatalogGroup, type CatalogSection } from './catalog-shell.js'

import AvatarMenu from '../../components/AvatarMenu.js'
import BrandMark from '../../components/BrandMark.js'
import { ConfirmDialog, useConfirm } from '../../components/ConfirmDialog.js'
import { DataTable, type ColumnDef } from '../../components/DataTable.js'
import { ErrorBoundary } from '../../components/ErrorBoundary.js'
import { FilterableTable, StatusChip } from '../../components/FilterableTable.js'
import { HelpTip } from '../../components/HelpTip.js'
import RefundDialog, { type RefundDialogOrder } from '../../components/RefundDialog.js'
import { SearchSelect, type SearchSelectOption } from '../../components/SearchSelect.js'
import ThemeSwitcher from '../../components/ThemeSwitcher.js'
import TrainingModeBadge from '../../components/TrainingModeBadge.js'
import { useTrainingModeContext } from '../../lib/training-mode.js'

type Row = { id: string; folio: string; total: number; status: 'PAID' | 'PENDING' | 'CANCELLED'; date: string }
const SAMPLE: Row[] = [
  { id: 't1', folio: 'A-1001', total: 45000, status: 'PAID', date: new Date().toISOString() },
  { id: 't2', folio: 'A-1002', total: 12000, status: 'PENDING', date: new Date().toISOString() },
  { id: 't3', folio: 'A-1003', total: 78000, status: 'CANCELLED', date: new Date(Date.now() - 86400000).toISOString() },
  { id: 't4', folio: 'A-1004', total: 25000, status: 'PAID', date: new Date(Date.now() - 86400000 * 3).toISOString() },
]

function pesos(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`
}

// ── Layout/Shell ─────────────────────────────────────────────────────────────

function SecPosLayout() {
  return (
    <CatalogBlock title="PosLayout — topbar + rail (miniatura)" note="El shell real ya envuelve esta página; aquí una miniatura estática (sin navegación) reusa las MISMAS clases CSS que inyecta PosLayout.">
      <p className="cat-muted" style={{ margin: 0 }}>
        Topbar: hamburguesa (móvil) + marca + tag + badge de entrenamiento (centro) + refrescar/bloquear/tema/avatar. Rail: iconos con label, activo = fill redondeado. Ver PosLayout en vivo: esta página misma.
      </p>
      <div style={{ width: '100%', maxWidth: 640, height: 300, border: '1px solid var(--pos-separator-strong)', borderRadius: 'var(--pos-radius-lg)', overflow: 'hidden', position: 'relative' }}>
        <div className="pos-shell" style={{ height: '100%' }}>
          <header className="pos-topbar">
            <div className="pos-topbar__brand">
              <BrandMark size="sm" />
              <span className="pos-topbar__tag">restaurante</span>
            </div>
            <div className="pos-topbar__center" />
            <div className="pos-topbar__meta">
              <span className="cat-muted">cajero@demo</span>
            </div>
          </header>
          <div className="pos-body">
            <nav className="pos-rail" aria-hidden>
              <div className="pos-rail__item pos-rail__item--active"><span className="pos-rail__label">Piso</span></div>
              <div className="pos-rail__item"><span className="pos-rail__label">Menú</span></div>
              <div className="pos-rail__item"><span className="pos-rail__label">Orden</span></div>
              <div className="pos-rail__item"><span className="pos-rail__label">Tickets</span></div>
            </nav>
            <main className="pos-content"><p className="cat-muted" style={{ margin: 0 }}>Contenido de la página…</p></main>
          </div>
        </div>
      </div>
    </CatalogBlock>
  )
}

// ── Overlays ─────────────────────────────────────────────────────────────────

function SecConfirmDialog() {
  const confirm = useConfirm()
  const [staticOpen, setStaticOpen] = useState<'default' | 'danger' | null>(null)
  return (
    <CatalogBlock title="ConfirmDialog — reemplazo de window.confirm">
      <CatalogGroup label="Promesa (useConfirm) — tone default / danger">
        <button className="cat-btn" onClick={async () => { await confirm({ title: '¿Continuar?', message: 'Esta acción no tiene tono destructivo.' }) }}>confirm() tone=default</button>
        <button className="cat-btn cat-btn--danger" onClick={async () => { await confirm({ title: '¿Cancelar orden?', message: 'No se puede deshacer.', tone: 'danger', confirmLabel: 'Cancelar orden' }) }}>confirm() tone=danger</button>
      </CatalogGroup>
      <CatalogGroup label="Instancia directa (estado fijo, para inspección visual)">
        <button className="cat-btn" onClick={() => setStaticOpen('default')}>Abrir estático (default)</button>
        <button className="cat-btn" onClick={() => setStaticOpen('danger')}>Abrir estático (danger)</button>
      </CatalogGroup>
      {staticOpen && (
        <ConfirmDialog
          title={staticOpen === 'danger' ? '¿Cancelar orden?' : '¿Aplicar descuento?'}
          message={staticOpen === 'danger' ? 'No se puede deshacer.' : 'Se aplicará de inmediato al total.'}
          tone={staticOpen}
          confirmLabel={staticOpen === 'danger' ? 'Cancelar orden' : 'Aplicar'}
          onConfirm={() => setStaticOpen(null)}
          onCancel={() => setStaticOpen(null)}
        />
      )}
      <style>{catBtnStyles}</style>
    </CatalogBlock>
  )
}

function SecRefundDialog() {
  const [open, setOpen] = useState(false)
  const order: RefundDialogOrder = { id: 'ord-demo-1', folio: 'A-1001', totalCents: 45000, paidCents: 45000 }
  return (
    <CatalogBlock title="RefundDialog — reembolso a nivel orden" note="Abrir el diálogo es seguro (sin red); el botón «Reembolsar» sí invoca el flujo real de PIN de gerente + POST /orders/:id/refund — no lo completes fuera de una prueba controlada.">
      <button className="cat-btn cat-btn--danger" onClick={() => setOpen(true)}>Abrir RefundDialog</button>
      <RefundDialog open={open} order={order} currentUserId="demo-user" onClose={() => setOpen(false)} />
      <style>{catBtnStyles}</style>
    </CatalogBlock>
  )
}

// ── Tablas/Datos ─────────────────────────────────────────────────────────────

function SecFilterableTable() {
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('ALL')
  return (
    <CatalogBlock title="FilterableTable (nui-*) — filtros + tabla + paginación">
      <CatalogGroup label="Búsqueda + estatus + rango de fechas + resumen + acciones por fila">
        <div style={{ width: '100%' }}>
          <FilterableTable<Row>
            rows={SAMPLE}
            getRowKey={(r) => r.id}
            columns={[
              { key: 'folio', header: 'Folio', sortable: true, sortAccessor: (r) => r.folio },
              { key: 'total', header: 'Total', align: 'right', sortable: true, sortAccessor: (r) => r.total, render: (r) => pesos(r.total) },
              { key: 'status', header: 'Estatus', render: (r) => <StatusChip tone={r.status === 'PAID' ? 'success' : r.status === 'PENDING' ? 'warning' : 'danger'}>{r.status}</StatusChip> },
            ]}
            searchable
            searchValue={search}
            onSearchChange={setSearch}
            searchAccessor={(r) => r.folio}
            searchPlaceholder="Buscar folio…"
            statusOptions={[{ value: 'ALL', label: 'Todos' }, { value: 'PAID', label: 'Pagado' }, { value: 'PENDING', label: 'Pendiente' }, { value: 'CANCELLED', label: 'Cancelado' }]}
            statusValue={status}
            onStatusChange={setStatus}
            statusAccessor={(r) => r.status}
            statusAllValue="ALL"
            dateRangeEnabled
            dateAccessor={(r) => r.date}
            summary={<span><strong>{SAMPLE.length}</strong> tickets · <em>demo</em></span>}
            rowActions={(r) => <button className="cat-btn cat-btn--sm">Ver {r.folio}</button>}
            ariaLabel="Tickets demo"
          />
        </div>
      </CatalogGroup>
      <CatalogGroup label="Estados — loading / vacío">
        <div style={{ flex: 1, minWidth: 280 }}>
          <FilterableTable<Row> rows={[]} getRowKey={(r) => r.id} columns={[{ key: 'folio', header: 'Folio' }]} loading />
        </div>
        <div style={{ flex: 1, minWidth: 280 }}>
          <FilterableTable<Row> rows={[]} getRowKey={(r) => r.id} columns={[{ key: 'folio', header: 'Folio' }]} emptyState="Sin tickets todavía" />
        </div>
      </CatalogGroup>
      <style>{catBtnStyles}</style>
    </CatalogBlock>
  )
}

function SecDataTableLegacy() {
  const cols: ColumnDef<Row>[] = [
    { key: 'folio', label: 'Folio', sortable: true },
    { key: 'total', label: 'Total', align: 'end', numeric: true, sortable: true, sortValue: (r) => r.total },
    { key: 'status', label: 'Estatus', hideOnNarrow: true },
  ]
  return (
    <CatalogBlock title="DataTable legacy (dhk8-dt)" note="Portado de sc-restaurant; incluye layout de CARDS para viewport angosto (534px tablet) que DataTable admin no tiene.">
      <CatalogGroup label="Con toolbar + cards en angosto">
        <div style={{ width: '100%' }}>
          <DataTable<Row>
            columns={cols}
            rows={SAMPLE}
            getRowKey={(r) => r.id}
            renderCell={(r, c) => (c.key === 'total' ? pesos(r.total) : c.key === 'status' ? r.status : r.folio)}
            zebra
            ariaLabel="Tickets (legacy)"
          />
        </div>
      </CatalogGroup>
      <CatalogGroup label="Estados — loading / vacío">
        <div style={{ flex: 1, minWidth: 280 }}><DataTable<Row> columns={cols} rows={[]} getRowKey={(r) => r.id} loading /></div>
        <div style={{ flex: 1, minWidth: 280 }}><DataTable<Row> columns={cols} rows={[]} getRowKey={(r) => r.id} emptyState="Sin registros." /></div>
      </CatalogGroup>
    </CatalogBlock>
  )
}

// ── Inputs/Pickers ───────────────────────────────────────────────────────────

function SecSearchSelect() {
  const options: SearchSelectOption[] = [
    { value: 'latte', label: 'Latte', hint: '$45.00' },
    { value: 'capuchino', label: 'Capuchino', hint: '$56.00' },
    { value: 'americano', label: 'Americano', hint: '$37.00' },
  ]
  const [text1, setText1] = useState('')
  const [text2, setText2] = useState('Producto no listado')
  return (
    <CatalogBlock title="SearchSelect — combobox con texto libre">
      <CatalogGroup label="Elegir de la lista / texto libre no inventariado">
        <div style={{ minWidth: 240 }}>
          <SearchSelect value={text1} options={options} onSelect={(o) => setText1(o.label)} onText={setText1} placeholder="Buscar producto…" />
        </div>
        <div style={{ minWidth: 240 }}>
          <SearchSelect value={text2} options={options} onSelect={(o) => setText2(o.label)} onText={setText2} placeholder="Buscar producto…" />
        </div>
      </CatalogGroup>
      <CatalogGroup label="loading / disabled">
        <div style={{ minWidth: 240 }}><SearchSelect value="" options={[]} onSelect={() => {}} onText={() => {}} loading placeholder="Cargando…" /></div>
        <div style={{ minWidth: 240 }}><SearchSelect value="" options={options} onSelect={() => {}} onText={() => {}} disabled placeholder="Deshabilitado" /></div>
      </CatalogGroup>
    </CatalogBlock>
  )
}

// ── Feedback ─────────────────────────────────────────────────────────────────

function SecHelpTip() {
  return (
    <CatalogBlock title="HelpTip — tooltip táctil (tap-to-toggle)">
      <CatalogGroup label="Texto simple / con mini-glosario">
        <span>Propina <HelpTip text="Se calcula sobre el subtotal antes de impuestos." /></span>
        <span>Split <HelpTip defs={[{ term: 'Por persona', desc: 'Divide el total entre N comensales.' }, { term: 'Por artículo', desc: 'Cada quien paga lo que ordenó.' }]} /></span>
      </CatalogGroup>
    </CatalogBlock>
  )
}

function SecTrainingModeBadge() {
  const { training, setTrainingMode } = useTrainingModeContext()
  return (
    <CatalogBlock title="TrainingModeBadge" note="El toggle de abajo activa el modo entrenamiento REAL de la sesión (persiste en localStorage) — usa «Salir» en el badge, o el mismo botón, para revertir.">
      <CatalogGroup label={`Estado actual: ${training ? 'ACTIVO' : 'inactivo (no renderiza nada)'}`}>
        <button className="cat-btn" onClick={() => setTrainingMode(!training)}>{training ? 'Desactivar modo entrenamiento' : 'Activar modo entrenamiento'}</button>
        <TrainingModeBadge />
      </CatalogGroup>
      <style>{catBtnStyles}</style>
    </CatalogBlock>
  )
}

// ── Navegación ───────────────────────────────────────────────────────────────

function SecAvatarMenu() {
  const [log, setLog] = useState('—')
  return (
    <CatalogBlock title="AvatarMenu — menú de cuenta (topbar)" note="Requiere sesión activa (useAuthStore) — como ya estás autenticado para ver /dev/components, renderiza con tus iniciales reales.">
      <CatalogGroup label="Click en el avatar para abrir el menú">
        <AvatarMenu onRefresh={() => setLog('Refrescar clickeado')} refreshing={false} onLock={() => setLog('Bloquear clickeado')} onLogout={() => setLog('Cerrar sesión clickeado')} />
        <span className="cat-muted">Última acción: {log}</span>
      </CatalogGroup>
    </CatalogBlock>
  )
}

// ── Marca ────────────────────────────────────────────────────────────────────

function SecBrandMark() {
  return (
    <CatalogBlock title="BrandMark — glifo + wordmark">
      <CatalogGroup label="size: sm / md / lg">
        <BrandMark size="sm" />
        <BrandMark size="md" />
        <BrandMark size="lg" />
      </CatalogGroup>
      <CatalogGroup label="glyphOnly / con tagline">
        <BrandMark size="md" glyphOnly />
        <BrandMark size="md" tagline="POS" />
      </CatalogGroup>
    </CatalogBlock>
  )
}

function SecThemeSwitcher() {
  return (
    <CatalogBlock title="ThemeSwitcher — Sol ↔ Luna">
      <CatalogGroup>
        <ThemeSwitcher />
        <span className="cat-muted">Mismo componente que el topbar real — alterna [data-theme] en &lt;html&gt;.</span>
      </CatalogGroup>
    </CatalogBlock>
  )
}

// ── Infra ────────────────────────────────────────────────────────────────────

function Boom({ armed }: { armed: boolean }): React.ReactElement {
  if (armed) throw new Error('Error de demostración (catálogo) — esto NO es un bug real')
  return <p className="cat-muted" style={{ margin: 0 }}>Todo bien — sin errores.</p>
}

function SecErrorBoundary() {
  const [armed, setArmed] = useState(false)
  return (
    <CatalogBlock title="ErrorBoundary — recuperación de errores de render">
      <CatalogGroup label="Estado normal vs. crash forzado (con Reintentar)">
        <button className="cat-btn" onClick={() => setArmed(false)}>Restablecer demo</button>
        <button className="cat-btn cat-btn--danger" onClick={() => setArmed(true)}>Forzar error</button>
      </CatalogGroup>
      <div style={{ width: '100%', maxWidth: 420 }}>
        <ErrorBoundary key={String(armed)}><Boom armed={armed} /></ErrorBoundary>
      </div>
      <style>{catBtnStyles}</style>
    </CatalogBlock>
  )
}

// ── Por revisar (DS-4: duplicados a unificar) ────────────────────────────────

function SecPorRevisar() {
  return (
    <>
      <CatalogBlock
        title="Tablas — 3 implementaciones (DS-4)"
        note="POS: FilterableTable (nui-*, la más completa: filtros + fechas + resumen + acciones por fila) y DataTable legacy (dhk8-dt, único con layout de cards en angosto). admin: DataTable (nui-table) — ver catálogo admin. Candidata a unificar: nui-table de FilterableTable, absorbiendo el layout de cards del legacy."
      >
        <CatalogGroup label="POS FilterableTable (referencia — resumen compacto)">
          <div style={{ width: '100%', maxWidth: 420 }}>
            <FilterableTable<Row> rows={SAMPLE.slice(0, 2)} getRowKey={(r) => r.id} columns={[{ key: 'folio', header: 'Folio' }, { key: 'total', header: 'Total', render: (r) => pesos(r.total) }]} />
          </div>
        </CatalogGroup>
      </CatalogBlock>

      <CatalogBlock
        title="StatusChip ↔ StatusPill"
        note="POS StatusChip (en FilterableTable): tone success/warning/danger/neutral, sin dot, texto en mayúsculas. admin StatusPill: tone ok/warn/danger/info/neutral, con dot + label — ver catálogo admin. Vocabulario de tonos distinto — unificar antes de mover a ui-restaurant."
      >
        <CatalogGroup label="POS StatusChip (referencia)">
          <StatusChip tone="success">PAID</StatusChip>
          <StatusChip tone="warning">PENDING</StatusChip>
          <StatusChip tone="danger">CANCELLED</StatusChip>
        </CatalogGroup>
      </CatalogBlock>

      <CatalogBlock
        title="SearchSelect ↔ EntityCombobox"
        note="POS SearchSelect: opciones síncronas (value/label/hint) + texto libre (onText) para lo no-inventariado. admin EntityCombobox: fetch async + getOptionValue/Label + renderOption custom — ver catálogo admin. Construidos por separado — mismo propósito (combobox buscable)."
      >
        <CatalogGroup label="POS SearchSelect (referencia)">
          <div style={{ minWidth: 240 }}>
            <SearchSelect value="" options={[{ value: 'latte', label: 'Latte' }]} onSelect={() => {}} onText={() => {}} placeholder="Buscar…" />
          </div>
        </CatalogGroup>
      </CatalogBlock>

      <CatalogBlock
        title="HelpTip ↔ FieldHelp"
        note="POS HelpTip: ícono (i) tap-to-toggle → burbuja portal (fixed, clamp a viewport), texto corto + mini-glosario opcional. admin FieldHelp: '?' junto al label → abre Sheet con summary/examples/commonMistakes (glosario largo) — ver catálogo admin. Mismo patrón, dos APIs y dos densidades de contenido."
      >
        <CatalogGroup label="POS HelpTip (referencia)">
          <HelpTip text="Ejemplo de ayuda corta." />
        </CatalogGroup>
      </CatalogBlock>
    </>
  )
}

const catBtnStyles = `
  .cat-btn {
    display: inline-flex; align-items: center; gap: 6px; min-height: 36px; padding: 0 var(--pos-space-3, 12px);
    border: 1px solid var(--pos-separator-strong); border-radius: var(--pos-radius-md, 8px);
    background: var(--pos-elevated); color: var(--pos-fg); font: inherit; font-size: var(--pos-text-sm, 13px); cursor: pointer;
  }
  .cat-btn:hover { background: var(--pos-surface); }
  .cat-btn--sm { min-height: 28px; padding: 0 var(--pos-space-2, 8px); font-size: var(--pos-text-xs, 12px); }
  .cat-btn--danger { border-color: color-mix(in oklch, var(--pos-danger) 45%, transparent); color: var(--pos-danger); background: transparent; }
  .cat-btn--danger:hover { background: var(--pos-danger-bg); }
`

// ── Registro de secciones ────────────────────────────────────────────────────

const SECTIONS: CatalogSection[] = [
  { id: 'pos-layout', label: 'PosLayout', category: 'Layout/Shell', render: () => <SecPosLayout /> },

  { id: 'confirm-dialog', label: 'ConfirmDialog', category: 'Overlays', render: () => <SecConfirmDialog /> },
  { id: 'refund-dialog', label: 'RefundDialog', category: 'Overlays', render: () => <SecRefundDialog /> },

  { id: 'filterable-table', label: 'FilterableTable', category: 'Tablas/Datos', render: () => <SecFilterableTable /> },
  { id: 'data-table-legacy', label: 'DataTable (legacy)', category: 'Tablas/Datos', render: () => <SecDataTableLegacy /> },

  { id: 'search-select', label: 'SearchSelect', category: 'Inputs/Pickers', render: () => <SecSearchSelect /> },

  { id: 'help-tip', label: 'HelpTip', category: 'Feedback', render: () => <SecHelpTip /> },
  { id: 'training-mode-badge', label: 'TrainingModeBadge', category: 'Feedback', render: () => <SecTrainingModeBadge /> },

  { id: 'avatar-menu', label: 'AvatarMenu', category: 'Navegación', render: () => <SecAvatarMenu /> },

  { id: 'brand-mark', label: 'BrandMark', category: 'Marca', render: () => <SecBrandMark /> },
  { id: 'theme-switcher', label: 'ThemeSwitcher', category: 'Marca', render: () => <SecThemeSwitcher /> },

  { id: 'error-boundary', label: 'ErrorBoundary', category: 'Infra', render: () => <SecErrorBoundary /> },

  { id: 'por-revisar', label: 'Duplicados (DS-4)', category: 'Por revisar', render: () => <SecPorRevisar /> },
]

export default function ComponentsGallery() {
  return (
    <CatalogShell
      title="Galería de componentes"
      subtitle="DS-3 — catálogo en vivo de la librería DS NintaiGO (POS). Compuerta de revisión antes de aplicar a las vistas."
      sections={SECTIONS}
    />
  )
}
